// src/components/admin/command_center/tabs/VideoLibraryTab.jsx
// NSL Video Library CRM — Sprint 3
// ✅ BLINDADO: Transcript import con timestamps exactos (H:MM:SS · M:SS · inline · LRC ms)
//    Misma garantía que SongLibraryTab: time_s int puro, cero float drift, orden monotónico.

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './VideoLibraryTab.css';

// ══════════════════════════════════════════════════════════════════════
//  CONSTANTS
// ══════════════════════════════════════════════════════════════════════

// ══════════════════════════════════════════════════════════════════════
//  LO QUE DIJO EL SERVIDOR
//
//  Aqui todos los errores se traducian a «❌ Error al guardar» o «❌ Error al
//  eliminar». Eso tira la unica informacion util: el backend responde 422
//  explicando que un `youtube_id` mal pegado se arregla copiando solo lo que
//  va detras de «v=». Convertir eso en «Error» manda al que lo lee a adivinar.
// ══════════════════════════════════════════════════════════════════════
async function loQueDijo(res, porDefecto) {
    try {
        const d = await res.json();
        if (d && d.detail) return String(d.detail);
    } catch { /* la respuesta no era JSON */ }
    return `${porDefecto} (HTTP ${res.status}).`;
}

const LEVELS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const LANGUAGES = ['English', 'Español'];
const TOPICS = ['business', 'tech', 'science', 'culture', 'history', 'health', 'environment', 'politics', 'sports', 'general'];

const LEVEL_CFG = {
    A1: { color: '#4ade80' }, A2: { color: '#86efac' },
    B1: { color: '#60a5fa' }, B2: { color: '#818cf8' },
    C1: { color: '#fb923c' }, C2: { color: '#f87171' },
};

const TOPIC_EMOJI = {
    business: '💼', tech: '💻', science: '🔬', culture: '🎨',
    history: '📜', health: '🏥', environment: '🌿', politics: '🏛️',
    sports: '⚽', general: '📚',
};

const EXERCISE_TYPE_CFG = {
    truefalse: { emoji: '⚖️', label: 'V/F', color: '#818cf8' },
    quiz: { emoji: '🎯', label: 'Quiz', color: '#34d399' },
    fill: { emoji: '✏️', label: 'Completar', color: '#fb923c' },
    wordselect: { emoji: '🔤', label: 'Vocabulario', color: '#60a5fa' },
};

// ══════════════════════════════════════════════════════════════════════
//  TRANSCRIPT PARSER — Reloj suizo 🕰️
//  Mirror exacto del parser de SongLibraryTab.
//
//  SOPORTA:
//    • M:SS          →  0:14  /  1:02
//    • H:MM:SS       →  1:02:30
//    • Inline        →  0:1414 seconds♪ Here comes...
//    • Multilínea    →  0:14 \n 14 seconds \n ♪ Here comes...
//    • LRC centisecs →  [01:14.35]  (milisegundos exactos)
//
//  GARANTÍAS:
//    • time_s  → SIEMPRE Number entero (Math.trunc en cada componente)
//    • time_ms → milisegundos exactos si el formato los provee
//    • Cero aritmética float — cero drift acumulado
//    • Orden monotónico garantizado (sort final)
//    • Sin duplicados (mismo time_ms → fusión de textos)
// ══════════════════════════════════════════════════════════════════════

const _TS_RE = /^(\d+):(\d{2})(?::(\d{2}))?/;
const _LRC_RE = /^\[(\d+):(\d{2})\.(\d{2,3})\]/;
const _DUR_LINE = /^\d+\s+(?:minute|second)s?(?:\s*,\s*\d+\s+(?:minute|second)s?)?$/i;
const _DUR_PREFIX = /^\d+\s+(?:minute|second)s?(?:\s*,\s*\d+\s+(?:minute|second)s?)?\s*/i;
const _NOISE = /[♪♫]+/g;
const _MULTI_WS = /\s{2,}/g;

function _tsToMs(m) {
    if (m[3] !== undefined) {
        const s = Math.trunc(+m[1]) * 3600 + Math.trunc(+m[2]) * 60 + Math.trunc(+m[3]);
        return { s, ms: s * 1000 };
    }
    const s = Math.trunc(+m[1]) * 60 + Math.trunc(+m[2]);
    return { s, ms: s * 1000 };
}

function _lrcToMs(m) {
    const min = Math.trunc(+m[1]);
    const sec = Math.trunc(+m[2]);
    const raw = m[3];
    const ms = raw.length === 3 ? Math.trunc(+raw) : Math.trunc(+raw) * 10;
    const total = (min * 60 + sec) * 1000 + ms;
    return { s: Math.trunc(total / 1000), ms: total };
}

function _clean(text) {
    return text.replace(_NOISE, ' ').replace(_MULTI_WS, ' ').trim();
}

export function parseTranscript(raw) {
    const lines = raw
        .replace(/^\uFEFF/, '')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n')
        .split('\n')
        .map(l => l.trim());

    const result = [];
    let curTime = null;
    let pending = [];

    function flush() {
        if (curTime && pending.length) {
            const clean = _clean(pending.join(' '));
            if (clean) result.push({ time_s: curTime.s, time_ms: curTime.ms, text: clean });
        }
        pending = [];
    }

    for (const line of lines) {
        if (!line) continue;

        const lrcMatch = line.match(_LRC_RE);
        if (lrcMatch) {
            flush();
            curTime = _lrcToMs(lrcMatch);
            const rest = _clean(line.slice(lrcMatch[0].length));
            if (rest) pending.push(rest);
            continue;
        }

        const tsMatch = line.match(_TS_RE);
        if (tsMatch) {
            flush();
            curTime = _tsToMs(tsMatch);
            let rest = line.slice(tsMatch[0].length).trim();
            if (!rest) continue;
            rest = rest.replace(_DUR_PREFIX, '').trim();
            if (rest) pending.push(rest);
            continue;
        }

        if (!curTime) continue;
        if (_DUR_LINE.test(line)) continue;
        pending.push(line);
    }
    flush();

    const map = new Map();
    for (const e of result) {
        if (map.has(e.time_ms)) {
            map.get(e.time_ms).text += ' ' + e.text;
        } else {
            map.set(e.time_ms, { ...e });
        }
    }

    return [...map.values()].sort((a, b) => a.time_ms - b.time_ms);
}

function _fmtMs(ms) {
    const total = Math.trunc(ms / 1000);
    const rem = ms % 1000;
    const m = Math.trunc(total / 60);
    const s = total % 60;
    return rem > 0
        ? `${m}:${String(s).padStart(2, '0')}.${String(rem).padStart(3, '0')}`
        : `${m}:${String(s).padStart(2, '0')}`;
}

// ══════════════════════════════════════════════════════════════════════
//  VIDEO TRANSCRIPT IMPORT PANEL
//  Guarda los entries como raw_transcript en edu_data del video.
//  Los segmentos se crean luego en el Edu Editor a partir de esta base.
// ══════════════════════════════════════════════════════════════════════

function VideoTranscriptImportPanel({ video, token, onImported }) {
    const [raw, setRaw] = useState('');
    const [preview, setPreview] = useState(null);
    const [status, setStatus] = useState('idle');
    const [errMsg, setErrMsg] = useState('');
    const [overwrite, setOverwrite] = useState(false);
    const taRef = useRef(null);

    const hasExisting = !!(video.edu_data?.raw_transcript);
    const existingSegs = video.edu_data?.segments?.length || 0;

    const handleParse = useCallback(() => {
        if (!raw.trim()) return;
        setStatus('parsing');
        setTimeout(() => {
            try {
                const entries = parseTranscript(raw);
                setPreview(entries);
                if (entries.length < 2) {
                    setStatus('error');
                    setErrMsg(`Solo se encontraron ${entries.length} entradas. Verifica el formato.`);
                } else {
                    setStatus('idle');
                    setErrMsg('');
                }
            } catch {
                setPreview(null);
                setStatus('error');
                setErrMsg('Error al parsear. Verifica que sea un transcript de YouTube.');
            }
        }, 0);
    }, [raw]);

    const handleSave = async () => {
        if (!preview || preview.length < 2) return;
        setStatus('saving');
        setErrMsg('');
        try {
            const authToken = token || localStorage.getItem('token');
            const res = await fetch(`/api/admin/edu-editor/${video.id}/transcript`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    raw_transcript: raw,
                    video_id: video.id,
                    overwrite,
                    entries: preview.map(e => ({ time_s: e.time_s, time_ms: e.time_ms, text: e.text })),
                }),
            });
            const data = await res.json();
            if (!res.ok) {
                setStatus('error');
                setErrMsg(data.detail || 'Error al guardar.');
                return;
            }
            setStatus('ok');
            if (onImported) onImported({ parsed_count: data.parsed_count });
        } catch {
            setStatus('error');
            setErrMsg('Error de red. Verifica la conexión.');
        }
    };

    const canParse = raw.trim().length > 10 && status !== 'saving';
    const canSave = preview?.length >= 2 && status === 'idle';
    const isOk = status === 'ok';

    const hasMs = preview?.some(e => e.time_ms % 1000 !== 0);
    const isMonotonic = preview?.every((e, i) => i === 0 || e.time_ms > preview[i - 1].time_ms) ?? true;

    return (
        <div className="vl-transcript-panel">
            {/* Instrucciones */}
            <div className="vl-tr-instr">
                <div className="vl-tr-instr-title">📹 Importar transcript del video</div>
                <ol className="vl-tr-steps">
                    <li>Abre el video en YouTube → <kbd>···</kbd> → <kbd>Mostrar transcripción</kbd></li>
                    <li>Selecciona todo (<kbd>Ctrl+A</kbd>) y copia (<kbd>Ctrl+C</kbd>)</li>
                    <li>Pega abajo y presiona <strong>Parsear</strong></li>
                    <li>Los tiempos se guardan con precisión exacta (reloj suizo 🕰️)</li>
                </ol>
                <div className="vl-tr-formats">
                    Soporta: <code>0:14</code> multilínea · <code>0:1414 seconds</code> inline ·
                    <code>1:02:30</code> H:MM:SS · <code>[01:14.35]</code> LRC ms
                </div>
            </div>

            {/* Textarea */}
            <div className="vl-tr-field">
                <label className="vl-tr-label">
                    Texto del transcript
                    {raw.length > 0 && (
                        <span className="vl-tr-chars">{raw.length.toLocaleString()} chars</span>
                    )}
                </label>
                <textarea
                    ref={taRef}
                    className="vl-tr-textarea"
                    value={raw}
                    rows={7}
                    spellCheck={false}
                    placeholder={"0:14\n14 seconds\nHere comes the sun\n0:21\n21 seconds\nIt's all right\n..."}
                    onChange={e => {
                        setRaw(e.target.value);
                        if (preview) { setPreview(null); setStatus('idle'); setErrMsg(''); }
                    }}
                />
            </div>

            {/* Botones */}
            <div className="vl-tr-row">
                <button
                    className="vl-tr-btn-parse"
                    disabled={!canParse}
                    onClick={handleParse}
                >
                    {status === 'parsing' ? '⏳ Parseando...' : '🔍 Parsear y previsualizar'}
                </button>
                {raw && (
                    <button
                        className="vl-tr-btn-clear"
                        onClick={() => { setRaw(''); setPreview(null); setStatus('idle'); setErrMsg(''); }}
                    >
                        Limpiar
                    </button>
                )}
            </div>

            {status === 'error' && errMsg && (
                <div className="vl-tr-error">⚠️ {errMsg}</div>
            )}

            {/* Preview */}
            {preview && preview.length >= 2 && (
                <div className="vl-tr-preview">
                    {/* Stats bar */}
                    <div className="vl-tr-statsbar">
                        <div className="vl-tr-stat">
                            <span className="vl-tr-stat-n">{preview.length}</span>
                            <span className="vl-tr-stat-l">entradas</span>
                        </div>
                        <div className="vl-tr-stat">
                            <span className="vl-tr-stat-n" style={{ color: '#34d399' }}>
                                {_fmtMs(preview[0].time_ms)}
                            </span>
                            <span className="vl-tr-stat-l">primer tiempo</span>
                        </div>
                        <div className="vl-tr-stat">
                            <span className="vl-tr-stat-n" style={{ color: '#a78bfa' }}>
                                {_fmtMs(preview[preview.length - 1].time_ms)}
                            </span>
                            <span className="vl-tr-stat-l">último tiempo</span>
                        </div>
                        <div className="vl-tr-stat">
                            <span className="vl-tr-stat-n" style={{ color: isMonotonic ? '#34d399' : '#f87171' }}>
                                {isMonotonic ? '✓' : '⚠'}
                            </span>
                            <span className="vl-tr-stat-l">monotónico</span>
                        </div>
                        <div className="vl-tr-stat">
                            <span className="vl-tr-stat-n" style={{ color: '#60a5fa' }}>
                                {hasMs ? 'ms' : 's'}
                            </span>
                            <span className="vl-tr-stat-l">precisión</span>
                        </div>
                    </div>

                    {/* Guarantee badge */}
                    <div className="vl-tr-guarantee">
                        🕰️ Todos los tiempos son <code>int</code> exactos — cero float, cero drift.
                        {hasMs && ' Milisegundos capturados con precisión LRC.'}
                    </div>

                    {/* Table */}
                    <div className="vl-tr-table-wrap">
                        <table className="vl-tr-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>time_ms</th>
                                    <th>MM:SS{hasMs ? '.mmm' : ''}</th>
                                    <th>Texto del segmento</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.slice(0, 60).map((e, i) => (
                                    <tr key={e.time_ms} className={i % 2 === 0 ? 'vl-tr-even' : ''}>
                                        <td className="vl-tr-idx">{i + 1}</td>
                                        <td className="vl-tr-ms"><code>{e.time_ms}</code></td>
                                        <td className="vl-tr-fmt">
                                            <span className="vl-tr-ts-pill">{_fmtMs(e.time_ms)}</span>
                                        </td>
                                        <td className="vl-tr-text">{e.text}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {preview.length > 60 && (
                            <div className="vl-tr-more">+ {preview.length - 60} entradas más</div>
                        )}
                    </div>

                    {/* Overwrite warning */}
                    {hasExisting && (
                        <label className="vl-tr-overwrite">
                            <input
                                type="checkbox"
                                checked={overwrite}
                                onChange={e => setOverwrite(e.target.checked)}
                            />
                            <span>
                                <strong>Sobreescribir</strong> — Este video ya tiene transcript guardado
                                {existingSegs > 0 && (
                                    <> y <strong>{existingSegs} segmentos</strong> en el Edu Editor</>
                                )}.
                                El transcript se reemplazará (los ejercicios no se borran).
                            </span>
                        </label>
                    )}

                    {/* Save button */}
                    {!isOk && (
                        <button
                            className="vl-tr-btn-save"
                            onClick={handleSave}
                            disabled={!canSave || (hasExisting && !overwrite)}
                        >
                            {status === 'saving'
                                ? '⏳ Guardando en DB...'
                                : `💾 Guardar ${preview.length} entradas en edu_data`}
                        </button>
                    )}

                    {isOk && (
                        <div className="vl-tr-success">
                            <span className="vl-tr-success-icon">✅</span>
                            <div>
                                <strong>Transcript guardado con tiempos exactos</strong><br />
                                {preview.length} entradas disponibles en el Edu Editor
                                para crear segmentos con ejercicios.
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  HELPERS
// ══════════════════════════════════════════════════════════════════════

const thumb = (ytId) => `https://img.youtube.com/vi/${ytId}/mqdefault.jpg`;

const fmtDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('es-MX', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
};

const fmtTime = (secs) => {
    if (!secs && secs !== 0) return '0:00';
    const total = Math.trunc(secs);          // ← Math.trunc, no Math.floor, consistente con el parser
    const m = Math.trunc(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
};

const getSegments = (video) => video.edu_data?.segments || [];

// ══════════════════════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ══════════════════════════════════════════════════════════════════════

function LevelBadge({ level }) {
    const cfg = LEVEL_CFG[level] || LEVEL_CFG.B1;
    return (
        <span className="vl-badge" style={{ '--bc': cfg.color }}>
            {level}
        </span>
    );
}

function ConfirmModal({ video, onConfirm, onCancel }) {
    return (
        <div className="vl-overlay" onClick={onCancel}>
            <div className="vl-confirm-box" onClick={e => e.stopPropagation()}>
                <div className="vl-confirm-icon">🗑️</div>
                <h3>Eliminar video</h3>
                <p>
                    ¿Seguro que quieres eliminar <strong>"{video.title}"</strong>?
                    Esta acción no se puede deshacer.
                </p>
                <div className="vl-confirm-btns">
                    <button className="vl-btn-ghost" onClick={onCancel}>Cancelar</button>
                    <button className="vl-btn-danger" onClick={onConfirm}>Sí, eliminar</button>
                </div>
            </div>
        </div>
    );
}

function EditModal({ video, token, onSave, onClose, onTranscriptImported }) {
    const [form, setForm] = useState({
        title: video.title,
        channel: video.channel || '',
        topic: video.topic || 'general',
        language: video.language,
        level: video.level,
        youtube_id: video.youtube_id,
    });
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [activeTab, setActiveTab] = useState('meta');

    const segments = getSegments(video);
    const typeCounts = segments.reduce((acc, s) => {
        acc[s.exercise_type] = (acc[s.exercise_type] || 0) + 1;
        return acc;
    }, {});
    const hasTranscript = !!(video.edu_data?.raw_transcript);

    const handleSave = async () => {
        setSaving(true);
        setSaveError('');
        try {
            const authToken = token || localStorage.getItem('token');
            const res = await fetch(`/api/admin/edu-editor/${video.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                // ── SOLO LOS METADATOS ──────────────────────────────
                // Antes iba `{ ...form, edu_data: video.edu_data }`: para
                // cambiar el titulo se reenviaban todos los segmentos con sus
                // ejercicios. Si la copia de esta pantalla estaba vieja
                // —porque alguien lo edito en el Edu Editor mientras tanto— se
                // pisaba el trabajo de preparar el video entero.
                //
                // El PUT ya era un parche (`exclude_unset`): lo que no se
                // manda, no se toca.
                body: JSON.stringify(form),
            });
            if (!res.ok) {
                setSaveError(`❌ ${await loQueDijo(res, 'No se pudo guardar')}`);
                setSaving(false);
                return;
            }
            const updated = await res.json();
            onSave(updated);
        } catch {
            setSaveError('❌ No se pudo hablar con el servidor. Nada ha cambiado.');
        }
        setSaving(false);
    };

    const field = (key, label, opts = {}) => (
        <div className="vl-form-field">
            <label>{label}</label>
            {opts.type === 'select' ? (
                <select
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                >
                    {opts.options.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                </select>
            ) : (
                <input
                    value={form[key]}
                    onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                    placeholder={opts.placeholder || ''}
                />
            )}
        </div>
    );

    const TABS = [
        { id: 'meta', label: '📝 Metadata' },
        { id: 'transcript', label: '📥 Transcript', dot: !hasTranscript },
        { id: 'segments', label: `📹 Segmentos (${segments.length})` },
    ];

    return (
        <div className="vl-overlay" onClick={onClose}>
            <div className="vl-modal" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="vl-modal-head">
                    <div className="vl-modal-thumb">
                        <img src={thumb(video.youtube_id)} alt="" />
                    </div>
                    <div className="vl-modal-title-wrap">
                        <div className="vl-modal-title">{video.title}</div>
                        <div className="vl-modal-sub">
                            {video.channel && <span>{video.channel} · </span>}
                            <LevelBadge level={video.level} />
                        </div>
                    </div>
                    <button className="vl-modal-close" onClick={onClose}>✕</button>
                </div>

                {/* Tabs */}
                <div className="vl-modal-tabs">
                    {TABS.map(t => (
                        <button
                            key={t.id}
                            className={`vl-modal-tab ${activeTab === t.id ? 'active' : ''} ${t.dot ? 'vl-modal-tab--dot' : ''}`}
                            onClick={() => setActiveTab(t.id)}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Tab: Metadata */}
                {activeTab === 'meta' && (
                    <div className="vl-modal-body">
                        {field('youtube_id', 'YouTube ID')}
                        {field('title', 'Título del video')}
                        {field('channel', 'Canal / Fuente', { placeholder: 'TED, BBC, CNN...' })}
                        {field('topic', 'Tema', {
                            type: 'select',
                            options: TOPICS.map(t => ({
                                value: t,
                                label: `${TOPIC_EMOJI[t] || '📚'} ${t.charAt(0).toUpperCase() + t.slice(1)}`,
                            })),
                        })}
                        <div className="vl-form-row">
                            {field('language', 'Idioma', {
                                type: 'select',
                                options: LANGUAGES.map(l => ({ value: l, label: l })),
                            })}
                            {field('level', 'Nivel', {
                                type: 'select',
                                options: LEVELS.map(l => ({ value: l, label: l })),
                            })}
                        </div>

                        {Object.keys(typeCounts).length > 0 && (
                            <div className="vl-type-breakdown">
                                <div className="vl-tb-label">Tipos de ejercicio</div>
                                <div className="vl-tb-badges">
                                    {Object.entries(typeCounts).map(([type, count]) => {
                                        const cfg = EXERCISE_TYPE_CFG[type] || { emoji: '❓', label: type, color: '#888' };
                                        return (
                                            <span key={type} className="vl-type-badge" style={{ '--tc': cfg.color }}>
                                                {cfg.emoji} {cfg.label}: {count}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab: Transcript Import */}
                {activeTab === 'transcript' && (
                    <div className="vl-modal-body vl-modal-body--noPad">
                        <VideoTranscriptImportPanel
                            video={video}
                            token={token}
                            onImported={(info) => onTranscriptImported?.(video.id, info)}
                        />
                    </div>
                )}

                {/* Tab: Segments */}
                {activeTab === 'segments' && (
                    <div className="vl-modal-body">
                        {segments.length === 0 ? (
                            <div className="vl-no-segs">Sin segmentos guardados</div>
                        ) : (
                            <>
                                <div className="vl-segs-hint">
                                    Vista de sólo lectura. Para corregir los ejercicios, cierra esto
                                    y pulsa 🪄 en el vídeo: se abre entero en el Edu Video Editor.
                                </div>
                                {segments.map((seg, i) => {
                                    const typeCfg = EXERCISE_TYPE_CFG[seg.exercise_type] || { emoji: '❓', label: seg.exercise_type, color: '#888' };
                                    return (
                                        <div key={i} className="vl-seg-row">
                                            <div className="vl-seg-header">
                                                <span className="vl-seg-num">#{i + 1}</span>
                                                <span className="vl-seg-time">
                                                    {fmtTime(seg.start_time)} → {fmtTime(seg.end_time)}
                                                </span>
                                                <span className="vl-seg-type" style={{ '--tc': typeCfg.color }}>
                                                    {typeCfg.emoji} {typeCfg.label}
                                                </span>
                                                <span className="vl-seg-status">
                                                    {seg.exercise ? '✅' : '⭕'}
                                                </span>
                                            </div>
                                            <p className="vl-seg-text">{seg.text?.slice(0, 120)}...</p>
                                            {seg.exercise?.question && (
                                                <p className="vl-seg-question">❓ {seg.exercise.question?.slice(0, 80)}...</p>
                                            )}
                                            {seg.exercise?.statement && (
                                                <p className="vl-seg-question">📋 {seg.exercise.statement?.slice(0, 80)}...</p>
                                            )}
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}

                {/* Footer */}
                <div className="vl-modal-foot">
                    {saveError && <span className="vl-save-error">{saveError}</span>}
                    <button className="vl-btn-ghost" onClick={onClose}>Cancelar</button>
                    {activeTab === 'meta' && (
                        <button className="vl-btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? '⏳ Guardando...' : '💾 Guardar cambios'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function VideoLibraryTab({ token, abrirEn }) {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterLevel, setFilterLevel] = useState('');
    const [filterLang, setFilterLang] = useState('');
    const [filterTopic, setFilterTopic] = useState('');
    const [editVideo, setEditVideo] = useState(null);
    const [deleteVideo, setDeleteVideo] = useState(null);
    const [toast, setToast] = useState({ text: '', type: '' });
    const [view, setView] = useState('table');

    const authToken = token || localStorage.getItem('token');

    const showToast = (text, type = 'info') => {
        setToast({ text, type });
        setTimeout(() => setToast({ text: '', type: '' }), 3500);
    };

    // ─ Publicar / despublicar ─────────────────────────────────────────
    // Despublicar NO destruye: el video se queda con sus segmentos y sus
    // ejercicios, y deja de aparecer en el catalogo del alumno.
    const handlePublicar = async (video) => {
        const quiero = !(video.is_published !== false);
        try {
            const res = await fetch(`/api/admin/edu-editor/${video.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`,
                },
                // Solo la marca: el PUT es un parche, los segmentos ni se tocan.
                body: JSON.stringify({ is_published: quiero }),
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo cambiar')}`, 'error');
                return;
            }
            setVideos(prev => prev.map(x =>
                x.id === video.id ? { ...x, is_published: quiero } : x));
            showToast(
                quiero
                    ? `🌍 "${video.title}" ya se ve en el catálogo`
                    : `🔒 "${video.title}" fuera del catálogo — sigue guardado`,
                'success',
            );
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
    };

    // ─ Fetch ──────────────────────────────────────────────────────────
    const fetchVideos = useCallback(async () => {
        setLoading(true);
        try {
            // Con techo: el servidor lo tiene desde hoy (`le=200`), y pedir
            // sin limite era traerse el catalogo entero.
            const res = await fetch('/api/admin/edu-editor/?limit=200', {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudieron cargar')}`, 'error');
                setLoading(false);
                return;
            }
            const data = await res.json();
            setVideos(data);
        } catch {
            showToast('❌ Error cargando videos', 'error');
        }
        setLoading(false);
    }, [authToken]);

    useEffect(() => { fetchVideos(); }, [fetchVideos]);

    // ─ Delete ─────────────────────────────────────────────────────────
    const handleDelete = async () => {
        if (!deleteVideo) return;
        try {
            const res = await fetch(`/api/admin/edu-editor/${deleteVideo.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) throw new Error();
            setVideos(prev => prev.filter(v => v.id !== deleteVideo.id));
            showToast(`🗑️ "${deleteVideo.title}" eliminado`, 'success');
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
        setDeleteVideo(null);
    };

    // ─ Duplicate ──────────────────────────────────────────────────────
    const handleDuplicate = async (video) => {
        try {
            const res = await fetch(`/api/admin/edu-editor/${video.id}/duplicate`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) throw new Error();
            const copy = await res.json();
            setVideos(prev => [copy, ...prev]);
            showToast(`📋 Copia de "${video.title}" creada`, 'success');
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
    };

    // ─ Corregir en el Edu Editor ──────────────────────────────────────
    //
    //  Rob, 06-10-2026: «¿qué pasa si cometí un error de ortografía, o realicé
    //  mal una pregunta, o se desfasó de tiempo? El botón de editar sólo me
    //  sirve para metadata... Con esto evitamos borrar el ejercicio y volver a
    //  crearlo de cero, eso sería una pérdida de tiempo y cometería más
    //  errores».
    //
    //  Se pide el vídeo ENTERO al servidor en vez de usar la copia de esta
    //  lista. Son dos líneas más y evitan lo peor que puede pasar aquí: abrir
    //  una copia vieja —de hace diez minutos, de antes de un cambio hecho en
    //  otra pestaña— y guardarla encima, pisando trabajo que ya estaba hecho.
    const [abriendo, setAbriendo] = useState(null);
    const corrigeEnElEditor = async (video) => {
        if (!abrirEn) return;
        setAbriendo(video.id);
        try {
            const res = await fetch(`/api/admin/edu-editor/${video.id}`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) {
                showToast(`❌ ${await loQueDijo(res, 'No se pudo abrir el vídeo')}`, 'error');
                setAbriendo(null);
                return;
            }
            abrirEn('Video Edu', { video: await res.json() });
        } catch {
            showToast('❌ No se pudo hablar con el servidor', 'error');
        }
        setAbriendo(null);
    };

    // ─ Save edit ──────────────────────────────────────────────────────
    const handleSaveEdit = (updated) => {
        setVideos(prev => prev.map(v => v.id === updated.id ? updated : v));
        setEditVideo(null);
        showToast('✅ Cambios guardados', 'success');
    };

    // ─ Transcript imported callback ───────────────────────────────────
    const handleTranscriptImported = useCallback(async (videoId, info) => {
        showToast(`📥 ${info.parsed_count} entradas importadas con tiempos exactos`, 'success');
        try {
            const res = await fetch(`/api/admin/edu-editor/${videoId}`, {
                headers: { 'Authorization': `Bearer ${authToken}` },
            });
            if (!res.ok) return;
            const updated = await res.json();
            setVideos(prev => prev.map(v => v.id === videoId ? updated : v));
            setEditVideo(prev => prev?.id === videoId ? updated : prev);
        } catch { /* DB guardó OK, solo el cache local queda stale */ }
    }, [authToken]);

    // ─ Filter ─────────────────────────────────────────────────────────
    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        return videos.filter(v => {
            if (q && !v.title.toLowerCase().includes(q) && !(v.channel || '').toLowerCase().includes(q)) return false;
            if (filterLevel && v.level !== filterLevel) return false;
            if (filterLang && v.language !== filterLang) return false;
            if (filterTopic && v.topic !== filterTopic) return false;
            return true;
        });
    }, [videos, search, filterLevel, filterLang, filterTopic]);

    // ─ Stats ──────────────────────────────────────────────────────────
    const stats = useMemo(() => {
        const byLevel = {};
        for (const v of videos) byLevel[v.level] = (byLevel[v.level] || 0) + 1;
        const totalSegs = videos.reduce((acc, v) => acc + (v.segments || 0), 0);
        return { total: videos.length, byLevel, totalSegs };
    }, [videos]);

    // ══════════════════════════════════════════════════════════════════
    //  RENDER
    // ══════════════════════════════════════════════════════════════════
    return (
        <div className="vl-root">

            {/* ── HEADER ── */}
            <header className="vl-header">
                <div className="vl-brand">
                    <span className="vl-brand-icon">🎬</span>
                    <div>
                        <div className="vl-brand-title">Video Library</div>
                        <div className="vl-brand-sub">NSL CRM · Videos educativos</div>
                    </div>
                </div>

                <div className="vl-header-stats">
                    <div className="vl-hs-pill">
                        <span className="vl-hs-n">{stats.total}</span>
                        <span className="vl-hs-l">videos</span>
                    </div>
                    <div className="vl-hs-pill">
                        <span className="vl-hs-n">{stats.totalSegs}</span>
                        <span className="vl-hs-l">segmentos</span>
                    </div>
                    {Object.entries(stats.byLevel).map(([l, n]) => (
                        <div key={l} className="vl-hs-badge" style={{ '--bc': LEVEL_CFG[l]?.color }}>
                            {l} ×{n}
                        </div>
                    ))}
                </div>

                <div className="vl-header-actions">
                    <button className={`vl-view-btn ${view === 'table' ? 'active' : ''}`} onClick={() => setView('table')}>☰</button>
                    <button className={`vl-view-btn ${view === 'grid' ? 'active' : ''}`} onClick={() => setView('grid')}>⊞</button>
                    <button className="vl-btn-refresh" onClick={fetchVideos} title="Recargar">🔄</button>
                </div>
            </header>

            {/* ── FILTERS ── */}
            <div className="vl-filters">
                <div className="vl-search-wrap">
                    <span className="vl-search-icon">🔍</span>
                    <input
                        className="vl-search"
                        placeholder="Buscar por título o canal..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="vl-search-clear" onClick={() => setSearch('')}>✕</button>
                    )}
                </div>

                <div className="vl-filter-chips">
                    <span className="vl-filter-label">Nivel:</span>
                    <button className={`vl-chip ${filterLevel === '' ? 'active' : ''}`} onClick={() => setFilterLevel('')}>
                        Todos
                    </button>
                    {LEVELS.map(l => (
                        <button
                            key={l}
                            className={`vl-chip ${filterLevel === l ? 'active' : ''}`}
                            style={{ '--cc': LEVEL_CFG[l]?.color }}
                            onClick={() => setFilterLevel(filterLevel === l ? '' : l)}
                        >
                            {l}
                        </button>
                    ))}
                </div>

                <div className="vl-filter-chips">
                    <span className="vl-filter-label">Idioma:</span>
                    {LANGUAGES.map(lang => (
                        <button
                            key={lang}
                            className={`vl-chip ${filterLang === lang ? 'active' : ''}`}
                            onClick={() => setFilterLang(filterLang === lang ? '' : lang)}
                        >
                            {lang === 'English' ? '🇺🇸' : '🇲🇽'} {lang}
                        </button>
                    ))}
                    {filterLang && (
                        <button className="vl-chip" onClick={() => setFilterLang('')}>Todos</button>
                    )}
                </div>

                <div className="vl-filter-chips">
                    <span className="vl-filter-label">Tema:</span>
                    <button className={`vl-chip ${filterTopic === '' ? 'active' : ''}`} onClick={() => setFilterTopic('')}>
                        Todos
                    </button>
                    {TOPICS.map(t => (
                        <button
                            key={t}
                            className={`vl-chip ${filterTopic === t ? 'active' : ''}`}
                            onClick={() => setFilterTopic(filterTopic === t ? '' : t)}
                        >
                            {TOPIC_EMOJI[t]} {t}
                        </button>
                    ))}
                </div>

                {(filterLevel || filterLang || filterTopic || search) && (
                    <div className="vl-results-count">
                        {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
                        <button className="vl-clear-all" onClick={() => {
                            setSearch(''); setFilterLevel(''); setFilterLang(''); setFilterTopic('');
                        }}>
                            Limpiar filtros
                        </button>
                    </div>
                )}
            </div>

            {/* ── CONTENT ── */}
            <div className="vl-content">

                {loading && (
                    <div className="vl-loading">
                        <div className="vl-spinner" />
                        <span>Cargando biblioteca...</span>
                    </div>
                )}

                {!loading && filtered.length === 0 && (
                    <div className="vl-empty">
                        <div className="vl-empty-icon">🎬</div>
                        <div className="vl-empty-title">
                            {videos.length === 0 ? 'Biblioteca vacía' : 'Sin resultados'}
                        </div>
                        <div className="vl-empty-sub">
                            {videos.length === 0
                                ? 'Crea tu primer video desde la pestaña Edu Editor'
                                : 'Intenta con otros filtros de búsqueda'}
                        </div>
                    </div>
                )}

                {!loading && filtered.length > 0 && view === 'table' && (
                    <div className="vl-table-wrap">
                        <table className="vl-table">
                            <thead>
                                <tr>
                                    <th className="vl-th-thumb">Portada</th>
                                    <th>Título / Canal</th>
                                    <th>Nivel</th>
                                    <th>Idioma</th>
                                    <th>Tema</th>
                                    <th className="vl-th-center">Segs.</th>
                                    <th>Creado</th>
                                    <th className="vl-th-actions">Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(video => (
                                    <VideoRow
                                        key={video.id}
                                        video={video}
                                        onEdit={() => setEditVideo(video)}
                                        onCorregir={() => corrigeEnElEditor(video)}
                                        abriendo={abriendo === video.id}
                                        onDelete={() => setDeleteVideo(video)}
                                        onDuplicate={() => handleDuplicate(video)}
                                        onPublicar={() => handlePublicar(video)}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {!loading && filtered.length > 0 && view === 'grid' && (
                    <div className="vl-grid">
                        {filtered.map(video => (
                            <VideoCard
                                key={video.id}
                                video={video}
                                onEdit={() => setEditVideo(video)}
                                onCorregir={() => corrigeEnElEditor(video)}
                                abriendo={abriendo === video.id}
                                onDelete={() => setDeleteVideo(video)}
                                onDuplicate={() => handleDuplicate(video)}
                                onPublicar={() => handlePublicar(video)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── MODALS ── */}
            {editVideo && (
                <EditModal
                    video={editVideo}
                    token={authToken}
                    onSave={handleSaveEdit}
                    onClose={() => setEditVideo(null)}
                    onTranscriptImported={handleTranscriptImported}
                />
            )}

            {deleteVideo && (
                <ConfirmModal
                    video={deleteVideo}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteVideo(null)}
                />
            )}

            {/* ── TOAST ── */}
            {toast.text && (
                <div className={`vl-toast vl-toast--${toast.type}`}>
                    {toast.text}
                </div>
            )}
        </div>
    );
}

// ── Row ───────────────────────────────────────────────────────────────
function VideoRow({ video, onEdit, onCorregir, abriendo, onDelete, onDuplicate, onPublicar }) {
    // Sin la marca —un video de antes de que existiera— se da por publicado,
    // que es como se comportaba. Un `undefined` no puede esconder contenido.
    const publicado = video.is_published !== false;
    return (
        <tr className="vl-tr">
            <td className="vl-td-thumb">
                <div className="vl-thumb-wrap">
                    <img
                        src={thumb(video.youtube_id)}
                        alt={video.title}
                        className="vl-thumb"
                        onError={e => { e.target.style.display = 'none'; }}
                    />
                    <a
                        href={`https://youtu.be/${video.youtube_id}`}
                        target="_blank" rel="noreferrer"
                        className="vl-thumb-play"
                    >▶</a>
                </div>
            </td>
            <td>
                <div className="vl-cell-title">{video.title}</div>
                <div className="vl-cell-sub">{video.channel || '—'}</div>
            </td>
            <td><LevelBadge level={video.level} /></td>
            <td>
                <span className="vl-lang-pill">
                    {video.language === 'English' ? '🇺🇸' : '🇲🇽'} {video.language}
                </span>
            </td>
            <td>
                <span className="vl-topic-pill">
                    {TOPIC_EMOJI[video.topic] || '📚'} {video.topic}
                </span>
            </td>
            <td className="vl-td-center">
                <span className="vl-num-badge">{video.segments ?? 0}</span>
            </td>
            <td className="vl-td-date">{fmtDate(video.created_at)}</td>
            <td className="vl-td-actions">
                <div className="vl-action-row">
                    <button
                        className="vl-act-btn"
                        onClick={onPublicar}
                        title={publicado
                            ? 'Quitar del catálogo del alumno (no se borra)'
                            : 'Publicar: el alumno podrá verlo'}
                        style={{ opacity: publicado ? 1 : 0.55 }}
                    >{publicado ? '🌍' : '🔒'}</button>
                    {/* Dos botones distintos a propósito: el lápiz cambia la
                        ficha —título, nivel, idioma— y la varita abre el vídeo
                        entero en el editor para corregir sus ejercicios. Antes
                        sólo estaba el primero y parecía que no se podía hacer
                        lo segundo. */}
                    <button className="vl-act-btn edit" onClick={onEdit} title="Editar la ficha: título, canal, nivel, idioma">✏️</button>
                    <button
                        className="vl-act-btn corregir"
                        onClick={onCorregir}
                        disabled={abriendo}
                        title="Corregir sus ejercicios en el Edu Video Editor"
                    >{abriendo ? '⏳' : '🪄'}</button>
                    <button className="vl-act-btn dup" onClick={onDuplicate} title="Duplicar">📋</button>
                    <button className="vl-act-btn delete" onClick={onDelete} title="Eliminar">🗑️</button>
                </div>
            </td>
        </tr>
    );
}

// ── Card ──────────────────────────────────────────────────────────────
function VideoCard({ video, onEdit, onCorregir, abriendo, onDelete, onDuplicate, onPublicar }) {
    const publicado = video.is_published !== false;
    const cfg = LEVEL_CFG[video.level] || LEVEL_CFG.B1;
    const segs = video.segments ?? 0;

    return (
        <div className="vl-card" style={{ '--dc': cfg.color }}>
            <div className="vl-card-thumb">
                <img
                    src={thumb(video.youtube_id)}
                    alt={video.title}
                    onError={e => { e.target.parentElement.style.background = '#1a1f2e'; }}
                />
                <div className="vl-card-overlay">
                    <button className="vl-act-btn edit" onClick={onEdit}>✏️ Ficha</button>
                    <button className="vl-act-btn corregir" onClick={onCorregir} disabled={abriendo}>
                        {abriendo ? '⏳ Abriendo…' : '🪄 Corregir ejercicios'}
                    </button>
                </div>
                <div className="vl-card-level-chip" style={{ '--bc': cfg.color }}>
                    {video.level}
                </div>
            </div>
            <div className="vl-card-body">
                <div className="vl-card-title">{video.title}</div>
                <div className="vl-card-channel">{video.channel || '—'}</div>
                <div className="vl-card-meta">
                    <span className="vl-lang-pill small">
                        {video.language === 'English' ? '🇺🇸' : '🇲🇽'}
                    </span>
                    <span className="vl-topic-pill small">
                        {TOPIC_EMOJI[video.topic] || '📚'} {video.topic}
                    </span>
                </div>
                <div className="vl-card-stats">
                    <span>{segs} segmentos</span>
                </div>
            </div>
            <div className="vl-card-foot">
                <button className="vl-act-btn dup    small" onClick={onDuplicate}>📋</button>
                <button className="vl-act-btn delete small" onClick={onDelete}>🗑️</button>
            </div>
        </div>
    );
}